import { IChatConfig } from '../../interfaces/chat-config-interfaces';
import { API_ENDPOINTS } from '../../config/api-config';
import { IChatQueueType } from '../../interfaces/case-details-api-interfaces';
import { getMIAWEnv } from '../session-storage-utils';

// Import fallback configs
import enAuConfig from '../../../resources/sf-config/en-au.json';
import enCaConfig from '../../../resources/sf-config/en-ca.json';
import enUsConfig from '../../../resources/sf-config/en-us.json';

// Fallback config mapping
const fallbackConfigs: Record<string, IChatConfig> = {
  'en-au': enAuConfig as IChatConfig,
  'en-ca': enCaConfig as IChatConfig,
  'en-us': enUsConfig as IChatConfig,
};

export const initChatConfig = async (locale: string) => {
  let sfConfig: IChatConfig;

  try {
    const res = await fetch(API_ENDPOINTS.SF_CONFIG(locale));
    if (!res.ok) {
      throw new Error('Failed to fetch SF config');
    }
    sfConfig = await res.json();
  } catch (error) {
    console.warn(`Failed to fetch SF config for locale ${locale}, using fallback config`, error);

    // Use fallback config if available
    const fallbackConfig = fallbackConfigs[locale];
    if (!fallbackConfig) {
      console.error(`No fallback config available for locale: ${locale}`);
      throw new Error(`Unable to load SF config for locale: ${locale}`);
    }
    sfConfig = fallbackConfig;
  }

  const { productionConfig, sandboxConfig, devsConfig } = sfConfig;
  const env = getMIAWEnv();

  if (env === 'sandbox') {
    return sandboxConfig;
  } else if (env === 'devs') {
    if (devsConfig) return devsConfig;
    console.warn('Dev config not found, falling back to sandbox config');
    return sandboxConfig;
  }
  return productionConfig;
};

export const getQueue = (
  selectedQueue: IChatQueueType,
  queueMapping?: {
    default: string;
    checkout?: string;
    design?: string;
    digital?: string;
    msp?: string;
    ppag?: string;
    wedding?: string;
  },
): string => {
  return queueMapping?.[selectedQueue] || queueMapping?.['default'] || '';
};

export const getQuestfromIntent = (intent: string): IChatQueueType => {
  switch (intent) {
    case 'checkout':
      return 'checkout';
    case 'design':
      return 'design';
    case 'digital':
      return 'digital';
    case 'order':
    default:
      return 'default';
  }
};
export const getQueueFromPageName = (pageName: string): IChatQueueType => {
  if (!pageName || pageName.trim() === '') {
    return getQueuefromURL();
  }

  const lowerCasePageName = pageName?.toLowerCase();
  if (lowerCasePageName.includes('checkout')) {
    return 'checkout';
  } else if (lowerCasePageName.includes('design') || lowerCasePageName.includes('studio') || lowerCasePageName.includes('projects')) {
    return 'design';
  } else if (lowerCasePageName.includes('digital') || lowerCasePageName.includes('subscriptions')) {
    return 'digital';
  } else if (lowerCasePageName.includes('msp') || lowerCasePageName.includes('mailing services')) {
    return 'msp';
  } else if (lowerCasePageName.includes('weddingDesignHelp')) {
    return 'wedding';
  }
  return 'default';
};

export const getQueuefromURL = (): IChatQueueType => {
  const pageURL = window?.location?.href?.toLowerCase();
  if (pageURL.includes('/c/') || pageURL.includes('/co/')) {
    return 'checkout';
  } else if (pageURL.includes('/studio') || pageURL.includes('/design')) {
    return 'design';
  } else if (pageURL.includes('/workspace') || pageURL.includes('/subscriptions')) {
    return 'digital';
  } else if (pageURL.includes('/msp/') || pageURL.includes('/am/')) {
    return 'msp';
  } else if (pageURL.includes('/wedding-design-services')) {
    return 'wedding';
  }
  return 'default';
};
